/**
 * KeywordReviewPreviewSlot
 *
 * Renders a fully interactive keyword review panel inside a chat behaviour slot
 * for the negative-keyword-analyzer agent. Unlike the bucket-based
 * content refresh/outline slots, this slot uses the approvals API directly.
 *
 * Features:
 * - Resolves workflowInstanceId from agent_short_name
 * - Renders KeywordReviewPanel in compact mode within the slot
 * - Supports fullscreen expansion via portal into the chat container
 * - Respects readOnly mode for historical chat messages
 *
 * Backend contract — minimal data required:
 * ```json
 * {
 *   "type": "agent_experience",
 *   "agent_short_name": "negative-keyword-analyzer",
 *   "experience": "keyword_review",
 *   "data": {}
 * }
 * ```
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { RowSelectionState } from '@tanstack/react-table';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { Button } from '@strivelabs/ui-core';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { useResolveWorkflowInstanceId } from '@/hooks/useResolveWorkflowInstanceId';
import { KeywordReviewPanel } from '@/components/keyword-review/KeywordReviewPanel';
import {
  ENABLE_MOCK_BEHAVIOUR,
  ENABLE_MOCK_KEYWORD_DATA,
  ACTIVE_MOCK_SCENARIO,
  MOCK_APPROVAL_ITEMS,
} from '@/constants/mockBehaviourChatData';
import {
  FullscreenSlotOverlay,
  SlotContainer,
} from './shared';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';

export interface KeywordReviewPreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  /** Behaviour action containing agent_short_name and optional data */
  action: BehaviourAgentExperienceAction;
  /** When true, disables all interactive elements (for historical chat messages) */
  readOnly?: boolean;
  /** Chat history ID for potential future state persistence */
  chatHistoryId?: string;
  /** Behaviour action state map (reserved for future use) */
  behaviourActionState?: Record<string, unknown>;
}

export function KeywordReviewPreviewSlot({
  slotId,
  action,
  readOnly,
  chatHistoryId,
}: KeywordReviewPreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);

  // Shared row selection state — persists across compact/expanded views
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const handleRowSelectionChange = useCallback((selection: RowSelectionState) => {
    setRowSelection(selection);
  }, []);

  // Resolve the workflow instance ID from the agent short name
  // (uses the globally loaded agentInstances from the app store)
  const agentShortName = action.agent_short_name;
  const resolvedWorkflowInstanceId = useResolveWorkflowInstanceId(agentShortName);
  const workflowInstanceId = resolvedWorkflowInstanceId ?? '';

  // When mock behaviour + keyword data flags are both on, inject mock approval items
  // so the panel renders a populated table without real backend data.
  const useMockData =
    ENABLE_MOCK_BEHAVIOUR &&
    ENABLE_MOCK_KEYWORD_DATA &&
    ACTIVE_MOCK_SCENARIO === 'keyword_review';
  const mockApprovalItems = useMockData ? MOCK_APPROVAL_ITEMS : undefined;

  // Optional category filter from the behaviour data.
  // Supports a single string or an array of category keys.
  // When provided, only keywords matching those categories are shown.
  const categoryFilter = action.data?.category;

  // Find the chat container element on mount for fullscreen portal target
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  // Guard: if workflow instance is not resolved yet, render nothing
  // (same pattern as content refresh/outline slots)
  if (!workflowInstanceId) return null;

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
            >
              <div className="p-4">
                <KeywordReviewPanel
                  workflowInstanceId={workflowInstanceId}
                  readOnly={readOnly}
                  categoryFilter={categoryFilter}
                  mockApprovalItems={mockApprovalItems}
                  rowSelection={rowSelection}
                  onRowSelectionChange={handleRowSelectionChange}
                />
              </div>
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------
  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand to fullscreen button — positioned at top right */}
        <Button
          variant="overlay"
          size="iconSm"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </Button>

        {/* Keyword review panel in compact mode */}
        <div className="p-4">
          <KeywordReviewPanel
            workflowInstanceId={workflowInstanceId}
            compact
            readOnly={readOnly}
            categoryFilter={categoryFilter}
            mockApprovalItems={mockApprovalItems}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
          />
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <KeywordReviewPanel
              workflowInstanceId={workflowInstanceId}
              readOnly={readOnly}
              categoryFilter={categoryFilter}
              mockApprovalItems={mockApprovalItems}
              rowSelection={rowSelection}
              onRowSelectionChange={handleRowSelectionChange}
            />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
